import { useState, useEffect, useCallback } from "react";
import {
  useParams,
  Link,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { Block } from "../../components";
import DebounceSearchInput from "../Constant/DebounceSearchInput";
import { Card, Row, Button, Col } from "react-bootstrap";
import BarLoader from "../../components/Loader/BarLoader";
import { FiEdit } from "react-icons/fi";
import {
  getVendorAssetsSummary,
  getVendorData,
} from "../../services/VendorServices";
import { toast } from "react-toastify";
import { FaDownload, FaEdit } from "react-icons/fa";
import EditVendor from "./EditVendor";
import Pagination from "../Constant/Pagination";
import { UnitStatusBadge } from "../../components";
import { FaArrowLeft } from "react-icons/fa";
import { CancelRequest } from "../../services/AxiosInstance";
import VendorAssetUnits from "./VendorAssetUnits";

function VendorAssetsSummary() {
  const { vendorId } = useParams();
  const [vendor, setVendor] = useState(null);
  const [assetUnits, setAssetUnits] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [vendorLoading, setVendorLoading] = useState(true);
  const [assetInfoLoading, setAssetInfoLoading] = useState(true);
  const [showUnitOffcanvas, setShowUnitOffcanvas] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const navigate = useNavigate();
  const [param, setParam] = useState({
    query: "",
    status: "",
    page: "1",
    page_size: "10",
  });

  useEffect(() => {
    const fetchAssetInfo = async () => {
      try {
        setAssetInfoLoading(true);
        const assetResp = await getVendorAssetsSummary(vendorId, param);
        setAssetUnits(assetResp.data?.results);
      } catch (error) {
        toast.error(error.response?.data?.message);
        setAssetUnits([]);
      } finally {
        setAssetInfoLoading(false);
      }
    };

    fetchAssetInfo();
  }, [param, vendorId]);

  const fetchVendorData = useCallback(async () => {
    try {
      setVendorLoading(true);
      const vendorResp = await getVendorData(vendorId);
      setVendor(vendorResp?.data);
    } catch (error) {
      toast.error(error.response?.data?.message);
      setVendor([]);
    } finally {
      setVendorLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendorData();
  }, [vendorId]);

  const handlePageChange = (page) => {
    setCurrentPage(page.selected + 1);
  };

  const handleSearchChange = (value) => {
    if (value.length > 0) CancelRequest();
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  const handleExport = async () => {
    try {
      const response = await getVendorAssetsSummary(vendorId, {
        export: "true",
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "vendor_asset_summary.xlsx";
      link.click();
    } catch (error) {
      console.error("Export failed", error);
    }
  };

  return (
    <div>
      <Block.Head>
        <Block.HeadBetween>
          <Block.HeadContent>
            {/* <button
                className="btn rounded-1 py-2 stylish-transparent-btn align-self-center"
                onClick={() => navigate(`/vendors`)}
              >
                <FaArrowLeft />
              </button> */}
            <Block.Title tag="h2">Vendors</Block.Title>
            <nav>
              <ol className="breadcrumb breadcrumb-arrow mb-0">
                <li className="breadcrumb-item">
                  <Link to="/">Home</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <Link to="/vendors">Vendors</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Asset Units
                </li>
              </ol>
            </nav>
          </Block.HeadContent>
          <Block.HeadContent>
            <ul className="d-flex"></ul>
          </Block.HeadContent>
        </Block.HeadBetween>
      </Block.Head>

      <Block className="mt-4">
        <div className="row">
          {/* Left: Vendor Info */}
          <div className="col-md-4 mb-4">
            <Card className="h-100">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex gap-2 align-items-center">
                    <Link
                      to="/vendors"
                      className="btn rounded-1 stylish-transparent-btn align-self-center btn-sm"
                      style={{ padding: "0.5rem 0.7rem" }}
                    >
                      <FaArrowLeft />
                    </Link>
                    <h5 className="mb-0">Vendor Information</h5>
                  </div>

                  <button
                    className="btn btn-outline-primary btn-sm align-self-center"
                    onClick={() => setShowEditModal(true)}
                  >
                    <FaEdit className="me-1" />
                    Edit
                  </button>
                </div>
                <hr></hr>
                {vendorLoading ? (
                  <div className="text-center my-4">
                    <BarLoader size={40} color="#255D5C" />
                  </div>
                ) : (
                  <>
                    <ul className="list-group list-group-borderless small">
                      <li className="list-group-item">
                        <span className="small w-40 d-inline-block">
                          Name :
                        </span>
                        <span className="text-darker fw-medium">
                          {vendor?.name}
                        </span>
                      </li>
                      <li className="list-group-item">
                        <span className="small w-40 d-inline-block">
                          Contact Person :
                        </span>
                        <span className="text-darker fw-medium">
                          {vendor?.contact_person_name}
                        </span>
                      </li>
                      <li className="list-group-item">
                        <span className="small w-40 d-inline-block">
                          Email :
                        </span>
                        <span className="text-darker fw-medium">
                          {vendor?.email}
                        </span>
                      </li>
                      <li className="list-group-item">
                        <span className="small w-40 d-inline-block">
                          Mobile :
                        </span>
                        <span className="text-darker fw-medium">
                          {vendor?.mobile_number}
                        </span>
                      </li>
                      <li className="list-group-item">
                        <span className="small w-40 d-inline-block">
                          Address :
                        </span>
                        <span className="text-darker fw-medium">
                          {vendor?.address}
                        </span>
                      </li>
                    </ul>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* Right: Asset Units */}
          <div className="col-md-8 mb-2">
            <Card>
              {/* Header: Title + Search + Export */}
              <div className="p-2 border-bottom">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                  <h5 className="mb-0 text-nowrap">Assets Summary</h5>

                  <div className="d-flex align-items-center gap-2">
                    <div style={{ maxWidth: "200px" }}>
                      <DebounceSearchInput
                        handleSearchChange={handleSearchChange}
                        style={{
                          height: "20px", // Slightly reduced from 35px
                          fontSize: "0.8rem", // Slightly smaller text
                          padding: "0.25rem 0.5rem", // Tighter padding
                        }}
                      />
                    </div>

                    <Button
                      className="py-1 px-2"
                      variant="outline-success"
                      onClick={() => handleExport(true)}
                      style={{ fontSize: "0.875rem", height: "42px" }}
                    >
                      <FaDownload />
                      <span className="ms-1">Export</span>
                    </Button>
                  </div>
                </div>
              </div>

              {/* Body: Table + Pagination */}
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-sm mb-0 align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Asset Name</th>
                        <th>In Stock</th>
                        <th>Assigned</th>
                        <th>Under Repair</th>
                        <th>Disposed</th>
                        <th>Total</th>
                        <th>Total Purchase Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assetInfoLoading ? (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            <BarLoader size={40} color="#255D5C" />
                          </td>
                        </tr>
                      ) : assetUnits?.length > 0 ? (
                        assetUnits.map((asset) => (
                          <tr key={asset.id}>
                            <td>
                              <Link
                                to={`/asset/${asset?.id}/basic-details`}
                                className="fw-bold text-primary"
                              >
                                {asset?.name || "-"}
                              </Link>
                            </td>
                            <td>
                              <span className="badge text-bg-success-soft rounded-pill">
                                {asset.in_stock_units || 0}
                              </span>
                            </td>
                            <td>
                              <span className="badge text-bg-info-soft rounded-pill">
                                {asset?.assigned_units || 0}
                              </span>
                            </td>
                            <td>
                              <span className="badge text-bg-warning-soft rounded-pill">
                                {asset?.under_repair_units || 0}
                              </span>
                            </td>
                            <td>
                              <span className="badge text-bg-danger-soft rounded-pill">
                                {asset?.disposed_units || 0}
                              </span>
                            </td>
                            <td>
                              <span className="badge text-bg-primary-soft rounded-pill">
                                {asset?.total_units || 0}
                              </span>
                            </td>
                            <td>
                              {asset?.total_purchase_cost
                                ? `₹${asset?.total_purchase_cost}`
                                : "-"}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="7" className="text-center py-4">
                            No data found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="mt-3">
                  <Pagination
                    currentPage={param.page}
                    recordsPage={param.page_size}
                    dataLength={total}
                    handlePagination={handlePageChange}
                  />
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </Block>

      <EditVendor
        showModal={showEditModal}
        setShowModal={setShowEditModal}
        vendorData={vendor}
        onVendorUpdated={(updated) => {
          fetchVendorData(); // Refresh list after edit
          setShowEditModal(false);
        }}
      />
    </div>
  );
}

export default VendorAssetsSummary;
